import { useEffect, useRef, useState, type RefObject } from 'react';
import { chapterAt, seekSecondsOf, stepChapter } from './chapterModel';
import { markDone, markSeen } from './progressModel';
import { StepList } from './StepList';
import { UpNext } from './UpNext';
import { progressStore } from './useLessonProgress';
import { StepNarration, usePlayerKeys, ViewerHeader, type ViewerProps } from './ViewerParts';
import { WalkthroughPlayer } from './WalkthroughPlayer';

/** The chapter the video is in, kept in progress as the viewer watches; and the way to jump to another one. */
function useChapters(lesson: ViewerProps['lesson'], chapters: number[], video: RefObject<HTMLVideoElement | null>) {
  const [index, setIndex] = useState(0);
  useEffect(() => {
    progressStore.update((m) => markSeen(m, lesson, index));
  }, [lesson, index]);
  const onTime = (seconds: number) => setIndex(chapterAt(chapters, seconds * 1000));
  const goTo = (i: number) => {
    const v = video.current;
    if (!v) return;
    v.currentTime = seekSecondsOf(chapters, i);
    setIndex(chapterAt(chapters, v.currentTime * 1000));
    void v.play().catch(() => undefined);
  };
  return { index, onTime, goTo };
}

/**
 * A video lesson (SPEC decision 27, §38.2): the recorded walkthrough of a task done in the real console, with its steps
 * as chapters. The step list seeks the video, the transcript follows what is playing, ←/→ move between chapters and
 * space plays or pauses; the lesson counts as done when the video ends. Up next names the following lesson.
 */
export function VideoLessonViewer({ lesson, nextLesson, progress, autoPlay, onClose, onOpenLesson, chapters }: ViewerProps & { chapters: number[] }) {
  const video = useRef<HTMLVideoElement | null>(null);
  const chapter = useChapters(lesson, chapters, video);
  const step = lesson.steps[chapter.index] ?? lesson.steps[0]!;
  usePlayerKeys((key) => {
    if (key === 'close') return onClose();
    const v = video.current;
    if (key === 'toggle') return void (v?.paused ? v.play().catch(() => undefined) : v?.pause());
    const next = stepChapter(chapters, chapter.index, key === 'next' ? 1 : -1);
    if (next !== null) chapter.goTo(next);
  });
  return (
    <section data-testid="lesson-viewer" data-lesson={lesson.id} data-format="video" className="flex min-w-0 flex-col gap-5">
      <ViewerHeader lesson={lesson} onClose={onClose} />
      <div className="grid min-w-0 gap-5 lg:grid-cols-[minmax(0,1fr)_18rem] lg:grid-rows-[auto_1fr] lg:items-start xl:grid-cols-[minmax(0,1fr)_20rem]">
        <div className="min-w-0 max-lg:order-1 lg:col-start-1 lg:row-start-1">
          <WalkthroughPlayer video={lesson.walkthrough!.video} audio={null} title={lesson.title} videoRef={video} poster={lesson.thumbnail} autoPlay={autoPlay} onTime={chapter.onTime} onEnded={() => progressStore.update((m) => markDone(m, lesson))} />
        </div>
        <div className="min-w-0 max-lg:order-3 lg:col-start-1 lg:row-start-2">
          <StepNarration step={step} index={chapter.index} />
        </div>
        <div className="flex min-w-0 flex-col gap-5 max-lg:contents lg:col-start-2 lg:row-span-2 lg:row-start-1">
          <UpNext next={nextLesson} onOpen={onOpenLesson} onClose={onClose} className="max-lg:order-2" />
          <div className="min-w-0 max-lg:order-4">
            <StepList steps={lesson.steps} index={chapter.index} seen={progress[lesson.id]?.seen ?? []} onGoTo={chapter.goTo} />
          </div>
        </div>
      </div>
    </section>
  );
}
